/* eslint-disable no-useless-escape */
import React, { useState } from 'react';
import {
	Box,
	Button,
	CssBaseline,
	Dialog,
	DialogContent,
	DialogTitle,
	Drawer,
	Fade,
	IconButton,
	Stack,
	StyledEngineProvider,
	ThemeProvider,
	Typography,
	useMediaQuery,
	useTheme,
	Hidden,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { RiArrowLeftLine, RiCloseLine } from 'react-icons/ri';
import produce from 'immer';
import { useDispatch, useSelector } from 'react-redux';
import { getOnlineLayoutSettings } from 'common/modules/customization/utils';
import { api } from 'common/frontend/api';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import SaveButton from 'common/components/SaveButton';
import { useTranslation } from 'services/localization';
import ContainerBox from 'components/ContainerBox';
import CustomizerPreview from './CustomizerPreview';
import ViewStoreBtn from 'components/ViewStoreBtn';
import CustomizerEditorPanel from './CustomizerEditorPanel';
import { FormValues } from './types';
import { removeNullOrUndefinedProperties } from './utils';
import { CustomizerContextProvider } from './CustomizerContext';

export interface Props {
	open: boolean;
	onClose: () => void;
}

const Customizer = (props: Props) => {
	const theme = useTheme();
	const { open, onClose } = props;
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const onlineLayout = useSelector(ShopSelectors.onlineLayout);
	const shopTheme = useSelector(ShopSelectors.shopTheme);
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

	const drawerWidth = 400;

	const dispatch = useDispatch();
	const { t } = useTranslation();

	const form = useForm<FormValues>({
		defaultValues: {
			selectedPage: 'home',
			onlineLayout: getOnlineLayoutSettings(onlineLayout, activeLocationId),
		},
		mode: 'onChange',
	});
	const { isSubmitting, dirtyFields, isValid, isDirty } = form.formState;

	const selectedPage = form.watch('selectedPage');
	const layoutSettings = form.watch('onlineLayout');

	const handleSubmit = async (data: FormValues) => {
		try {
			const onlineLayoutPath = `layouts.ONLINE.locations.${activeLocationId}` as `layouts.ONLINE.locations.${123}`;

			if (!!dirtyFields.onlineLayout) {
				const sanitizedData = produce(data.onlineLayout, (draft) => {
					draft.footer?.links?.linkObjects?.map((link) => link.url?.replace(/^(javascript\:)/, ''));
					draft.featuredImage.button?.src?.replace(/^(javascript\:)/, '');
					draft.featuredImage.src?.replace(/^(javascript\:)/, '');
				});
				const layoutWithOnlyDefinedProperties = produce(sanitizedData, (draft) => {
					removeNullOrUndefinedProperties(draft);
				});
				const updateObject = { [onlineLayoutPath]: layoutWithOnlyDefinedProperties };

				await api().shops.doc(shopId).public.update(updateObject);
			}
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	const renderMobileDrawer = () => {
		return (
			<Drawer
				sx={{
					display: { xs: 'block', md: 'none' },
					p: (theme) => theme.spacing(2),
					zIndex: 9999,
				}}
				anchor="left"
				open={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				variant="temporary"
				ModalProps={{
					keepMounted: true,
				}}
			>
				<Box sx={{ width: '100%', p: 4, pt: 8 }}>
					<IconButton
						sx={{
							position: 'absolute',
							top: (theme) => theme.spacing(1),
							right: (theme) => theme.spacing(1),
						}}
						onClick={() => setIsDrawerOpen(false)}
						size="large"
					>
						<RiCloseLine size={28} color="black" />
					</IconButton>
					<CustomizerEditorPanel />
				</Box>
			</Drawer>
		);
	};

	const renderDesktopSidepanel = () => {
		return (
			<Box sx={{ width: drawerWidth }}>
				<ContainerBox
					sx={{
						height: '100%',
						overflowY: 'scroll',
						backgroundColor: (theme) => theme.palette.background.paper,
						border: 'none',
					}}
					style={{ borderRadius: 0 }}
					margin="none"
				>
					<CustomizerEditorPanel />
				</ContainerBox>
			</Box>
		);
	};

	return (
		<Dialog fullScreen open={open} onClose={onClose}>
			<DialogTitle
				sx={{
					borderBottom: (theme) => `1px solid ${theme.palette.border.paper} `,
				}}
			>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Button sx={{ minWidth: 0 }} onClick={onClose}>
						<RiArrowLeftLine size={20} />
						{t('common:actions.exit')}
					</Button>
					<ViewStoreBtn />
					<SaveButton
						loading={isSubmitting}
						onClick={form.handleSubmit(handleSubmit)}
						t={t}
						disabled={!isValid || !isDirty}
					/>
				</Stack>
			</DialogTitle>
			<DialogContent
				sx={{
					position: 'relative',
					backgroundColor: theme.palette.background.default,
					height: '100%',
					p: 0,
				}}
			>
				<CustomizerContextProvider>
					<FormProvider {...form}>
						<Stack
							direction="row"
							sx={{ alignItems: 'stretch', height: '100%', overflow: 'hidden' }}
						>
							<Hidden mdUp>{renderMobileDrawer()}</Hidden>
							<Hidden mdDown>{renderDesktopSidepanel()}</Hidden>
							<Box
								sx={{
									flex: 1,
									p: isMobile ? 2 : 4,
									height: '100%',
									width: '100%',
									overflowY: 'scroll',
								}}
							>
								<Hidden mdUp>
									<Box my={2}>
										<Fade appear={false} in={isMobile}>
											<Button onClick={() => setIsDrawerOpen(true)} variant="contained">
												{t('common:actions.edit')}
											</Button>
										</Fade>
									</Box>
								</Hidden>
								<Typography>{t('channels.online.customizer.preview', 'Preview')}</Typography>
								<StyledEngineProvider injectFirst>
									<CssBaseline />
									<ThemeProvider theme={shopTheme}>
										<CustomizerPreview
											layoutSettings={layoutSettings}
											selectedPage={selectedPage}
										/>
									</ThemeProvider>
								</StyledEngineProvider>
							</Box>
						</Stack>
					</FormProvider>
				</CustomizerContextProvider>
			</DialogContent>
		</Dialog>
	);
};

export default Customizer;
